import { type QueryClient, MutationObserverOptions } from "@tanstack/react-query";
import { ModelName, MutationMethodForModel, ParamsForMethod, ReturnForMethod } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for setting mutation defaults.
 *
 * @template Model - The Tellescope model name (must support mutation methods)
 * @template Method - The mutation method for the model
 * @template TError - The error type for the mutation
 * @template TData - The data type returned by the mutation
 * @template TVariables - The variables type for the mutation
 * @template TOnMutateResult - The context type for the mutation
 */
export interface SetTellescopeMutationDefaultsArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
  TVariables = ParamsForMethod<Model, Method>,
  TOnMutateResult = unknown,
> {
  /** The Tellescope model name */
  model: Model;
  /** Mutation method name */
  method: Method;
  /** The default mutation options to set */
  defaults: Partial<Omit<MutationObserverOptions<TData, TError, TVariables, TOnMutateResult>, "mutationKey">>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Sets default options for matching Tellescope mutations.
 * These defaults will be applied to all mutations that match the specified key.
 * Mutation defaults are useful for setting default configurations like retry, onSuccess, onError, etc.
 *
 * Mutation keys follow the same structure as query keys: [model, method]
 *
 * @param args - The set mutation defaults arguments
 *
 * @example
 * ```tsx
 * // Set defaults for all createOne mutations
 * setTellescopeMutationDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "createOne",
 *   defaults: {
 *     retry: 3,
 *     onSuccess: () => {
 *       console.log("User created successfully");
 *     },
 *   },
 * });
 *
 * // Set defaults with error handler
 * setTellescopeMutationDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "updateOne",
 *   defaults: {
 *     retry: 1,
 *     onError: (error) => {
 *       console.error("Update failed:", error);
 *     },
 *   },
 * });
 *
 * // Set multiple mutation options
 * setTellescopeMutationDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "deleteOne",
 *   defaults: {
 *     retry: 0,
 *     onSuccess: (data, variables, context) => {
 *       console.log("Deleted:", variables);
 *     },
 *     onError: (error) => {
 *       console.error("Delete failed:", error);
 *     },
 *   },
 * });
 * ```
 */
export const setTellescopeMutationDefaults = <
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
  TVariables = ParamsForMethod<Model, Method>,
  TOnMutateResult = unknown,
>({
  model,
  method,
  defaults,
  queryClient,
}: SetTellescopeMutationDefaultsArgs<Model, Method, TError, TData, TVariables, TOnMutateResult>) => {
  // Use the same key structure as queries: [model, method]
  // Mutations don't include request params in the key
  const mutationKey = buildTellescopeQueryKey({ model, method });

  queryClient.setMutationDefaults(mutationKey, defaults);
};
